import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Snackbar,
  Alert,
  IconButton,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BugReportIcon from '@mui/icons-material/BugReport';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import ChatIcon from '@mui/icons-material/Chat';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useParams } from 'react-router-dom';
import { feedbackSchema, type FeedbackFormData } from './feedbackSchema';
import { useCreateFeedback } from '@/hooks/useFeedback';
import styles from './FeedbackModal.module.scss';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmitSuccess: () => void;
  screenshotBase64: string | null;
  consoleErrors: object[];
  networkRequests: object[];
  deviceInfo: object | null;
  /** Prefix prepended to the message when sent (e.g. "[מעבר לאתר הישן]"). */
  contextPrefix?: string;
}

type FeedbackType = 'Bug' | 'Suggestion' | 'General';

const FeedbackModal: React.FC<FeedbackModalProps> = ({
  isOpen,
  onClose,
  onSubmitSuccess,
  screenshotBase64,
  consoleErrors,
  networkRequests,
  deviceInfo,
  contextPrefix,
}) => {
  const { t } = useTranslation();
  const { fundId } = useParams<{ fundId: string }>();
  const createFeedback = useCreateFeedback();
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FeedbackFormData>({
    resolver: zodResolver(feedbackSchema),
    defaultValues: {
      type: undefined,
      message: '',
      missingFeature: '',
      includeScreenshot: true,
    },
    mode: 'onChange',
  });

  const selectedType = watch('type');
  const includeScreenshot = watch('includeScreenshot');

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (data: FeedbackFormData) => {
    try {
      const rawMessage = data.message.trim();
      const message = contextPrefix ? `${contextPrefix} ${rawMessage}` : rawMessage;
      await createFeedback.mutateAsync({
        message,
        missingFeature: data.missingFeature?.trim() || undefined,
        type: data.type,
        pageUrl: window.location.pathname + window.location.search,
        fundId: fundId ? parseInt(fundId, 10) : null,
        screenshotBase64: data.includeScreenshot ? (screenshotBase64 ?? undefined) : undefined,
        deviceInfo: deviceInfo ?? undefined,
        consoleErrors,
        networkRequests,
      });

      setShowSuccess(true);
      reset();
      onSubmitSuccess();
    } catch {
      setShowError(true);
    }
  };

  const typeChips: { value: FeedbackType; labelKey: string; icon: React.ReactElement }[] = [
    { value: 'Bug', labelKey: 'feedback.types.bug', icon: <BugReportIcon fontSize="small" /> },
    { value: 'Suggestion', labelKey: 'feedback.types.suggestion', icon: <LightbulbIcon fontSize="small" /> },
    { value: 'General', labelKey: 'feedback.types.general', icon: <ChatIcon fontSize="small" /> },
  ];

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        dir="rtl"
        className={styles.dialog}
      >
        <DialogTitle className={styles.title}>
          {t('feedback.title')}
          <IconButton onClick={handleClose} className={styles.closeBtn}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent className={styles.content}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className={styles.typeChipsWrapper}>
              <div className={styles.typeChips}>
                {typeChips.map((chip) => (
                  <Chip
                    key={chip.value}
                    label={t(chip.labelKey)}
                    icon={chip.icon}
                    variant={selectedType === chip.value ? 'filled' : 'outlined'}
                    color={selectedType === chip.value ? 'primary' : 'default'}
                    onClick={() => setValue('type', chip.value, { shouldValidate: true })}
                    className={styles.typeChip}
                    sx={
                      selectedType === chip.value
                        ? {
                            backgroundColor:
                              chip.value === 'Bug' ? '#d32f2f'
                              : chip.value === 'Suggestion' ? '#1976d2'
                              : 'var(--surface-primary)',
                            color: '#fff',
                            '& .MuiChip-icon': { color: '#fff' },
                          }
                        : {}
                    }
                  />
                ))}
              </div>
              {errors.type && <span className={styles.typeError}>{t(errors.type.message ?? '')}</span>}
            </div>

            <Controller
              name="message"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('feedback.description')}
                  fullWidth
                  multiline
                  rows={4}
                  placeholder={t('feedback.descriptionPlaceholder')}
                  size="small"
                  className={styles.field}
                  error={!!errors.message}
                  helperText={errors.message ? t(errors.message.message ?? '') : ''}
                  slotProps={{ htmlInput: { dir: 'rtl' } }}
                />
              )}
            />

            <Controller
              name="missingFeature"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('feedback.missingFeature')}
                  fullWidth
                  multiline
                  rows={3}
                  placeholder={t('feedback.missingFeaturePlaceholder')}
                  size="small"
                  className={styles.field}
                  helperText={t('feedback.missingFeatureHint')}
                  slotProps={{ htmlInput: { dir: 'rtl' } }}
                />
              )}
            />

            <div className={styles.screenshotSection}>
              <div className={styles.screenshotThumb}>
                {screenshotBase64 ? (
                  <img
                    src={`data:image/png;base64,${screenshotBase64}`}
                    alt={t('feedback.screenshot')}
                    className={`${styles.screenshotImg} ${!includeScreenshot ? styles.screenshotDisabled : ''}`}
                  />
                ) : (
                  <div className={styles.screenshotPlaceholder}>{t('feedback.screenshotUnavailable')}</div>
                )}
              </div>
              <Controller
                name="includeScreenshot"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value && !!screenshotBase64}
                        onChange={(e) => field.onChange(e.target.checked)}
                        size="small"
                        disabled={!screenshotBase64}
                        sx={{
                          '&.Mui-checked': {
                            color: 'var(--surface-primary)',
                          },
                        }}
                      />
                    }
                    label={t('feedback.includeScreenshot')}
                    className={styles.screenshotCheckbox}
                  />
                )}
              />
            </div>

          </form>
        </DialogContent>

        <DialogActions className={styles.actions}>
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={isSubmitting}
            sx={{
              color: 'var(--font-body-2)',
              borderColor: 'var(--border-low)',
              '&:hover': { borderColor: 'var(--border-strong)' },
            }}
          >
            {t('feedback.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit(onSubmit)}
            disabled={isSubmitting}
            sx={{
              backgroundColor: 'var(--surface-primary)',
              '&:hover': { backgroundColor: 'var(--surface-primary-dark)' },
            }}
          >
            {isSubmitting ? <CircularProgress size={20} color="inherit" /> : t('feedback.submit')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={showSuccess} autoHideDuration={4000} onClose={() => setShowSuccess(false)}>
        <Alert severity="success" onClose={() => setShowSuccess(false)} sx={{ direction: 'rtl' }}>
          {t('feedback.successMessage')}
        </Alert>
      </Snackbar>

      <Snackbar open={showError} autoHideDuration={4000} onClose={() => setShowError(false)}>
        <Alert severity="error" onClose={() => setShowError(false)} sx={{ direction: 'rtl' }}>
          {t('feedback.errorMessage')}
        </Alert>
      </Snackbar>
    </>
  );
};

export default FeedbackModal;
